// VerificationPage — /verification
// The public trust surface: what a Still Waters Academy credential means,
// how it's verified, and how it compares to traditional accreditation.
// Linked from the footer beside Terms and Privacy.
import { Link } from 'react-router-dom'
import {
  ShieldCheck, Eye, Lock, Award, Clock, TrendingUp, RotateCcw,
  Users, GraduationCap, Building2, CheckCircle2, AlertCircle, ChevronRight,
  Search, FileText, Camera, KeyRound, BarChart3,
} from 'lucide-react'
import SEO from '@/components/layout/SEO'

const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const GR = '#4ade80'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"
const sans = "'Inter', system-ui, sans-serif"

export default function VerificationPage() {
  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: sans }}>
      <SEO
        title="Credential Verification & Standards | Still Waters Academy"
        description="How Still Waters Academy credentials are issued, verified, and governed — every assessment, every attempt, every recovery, permanently on record and publicly confirmable."
      />

      {/* ═══ HERO ═══ */}
      <div style={{ background: `linear-gradient(135deg, #0A0D17 0%, #141B2E 100%)`, borderBottom: `1px solid ${BR}`, padding: '80px 24px 60px', textAlign: 'center' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', color: GR, fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 24 }}>
            <ShieldCheck size={12} /> The most verifiable education records in existence
          </div>
          <h1 style={{ fontFamily: serif, fontSize: 'clamp(2.2rem, 5vw, 3.6rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 20px', color: TX }}>
            Credential Verification & Standards
          </h1>
          <p style={{ color: MU, fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: 1.7, maxWidth: 580, margin: '0 auto' }}>
            Where traditional credentials ask you to trust the institution, we invite you
            to verify the evidence. Every Still Waters Academy credential is backed by a
            permanent, system-generated learning record — every assessment, every attempt,
            every recovery, timestamped and publicly confirmable.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '56px 24px 80px' }}>
        {/* ═══ SECTION 1: Comparison to traditional accreditation ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            How this compares to traditional accreditation
          </h2>
          <p style={{ color: MU, fontSize: 15, lineHeight: 1.7, margin: '0 0 24px' }}>
            Accreditation was built to solve a problem of its era: how to trust a claim
            when you can't see the work. Regional accreditors vouched for institutions;
            institutions vouched for students; employers trusted the chain. We replace
            the chain with direct evidence.
          </p>

          <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: `1px solid ${BR}`, background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ padding: '14px 20px', fontSize: 11, fontWeight: 800, color: MU2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Traditional credential
              </div>
              <div style={{ padding: '14px 20px', fontSize: 11, fontWeight: 800, color: G, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Still Waters credential
              </div>
            </div>
            {[
              ['Says "B+ in the course"', 'Shows every answer, every score, every retry'],
              ['Graded by a teaching assistant', 'Scored by the server — no human discretion, no client tampering'],
              ['Stored in a filing cabinet', 'Permanently recorded, timestamped, publicly confirmable'],
              ['Verified by phoning the registrar', 'Verified by scanning a QR code — 30 seconds, zero calls'],
              ['Content frozen for the degree\'s duration', 'Courses regenerate — content is renewable, evidence is permanent'],
              ['The institution vouches for the student', 'The evidence speaks for itself'],
            ].map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: i < 5 ? `1px solid rgba(255,255,255,0.04)` : 'none' }}>
                <div style={{ padding: '14px 20px', fontSize: 12.5, color: MU, lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertCircle size={12} color="#f87171" style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }} />
                  {row[0]}
                </div>
                <div style={{ padding: '14px 20px', fontSize: 12.5, color: TX, lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <CheckCircle2 size={12} color={GR} style={{ flexShrink: 0, marginTop: 2 }} />
                  {row[1]}
                </div>
              </div>
            ))}
          </div>

          <p style={{ color: MU2, fontSize: 12, lineHeight: 1.6, margin: '16px 0 0', fontStyle: 'italic' }}>
            We do not claim institutional accreditation. We claim something more useful:
            evidence. Where a traditional transcript asks you to trust the institution,
            a Still Waters record invites you to verify the work.
          </p>
        </section>


        {/* ═══ SECTION 2: What a credential means ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            What a Still Waters credential certifies
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              {
                icon: <Award size={18} />,
                title: 'Mastery, not completion',
                body: 'A certificate is issued only when a learner achieves 90% mastery — a rolling score that blends their entire quiz history. Completion without understanding earns nothing.',
              },
              {
                icon: <BarChart3 size={18} />,
                title: 'Server-scored assessments',
                body: 'Every quiz is generated server-side, scored server-side against answer keys the learner never sees. The client cannot write scores. The client cannot see correct answers. Grades are earned, not entered.',
              },
              {
                icon: <Clock size={18} />,
                title: 'Permanent, timestamped evidence',
                body: 'Each lesson, each quiz, each retry is recorded with a timestamp in an immutable learning log. The record begins the moment a learner starts and never edits itself.',
              },
              {
                icon: <RotateCcw size={18} />,
                title: 'Retries are documented, not hidden',
                body: 'A learner who fails and recovers has both events on record. We treat documented persistence as evidence of character, not a blemish to erase.',
              },
              {
                icon: <TrendingUp size={18} />,
                title: 'Verified learning hours',
                body: 'Total instruction time is aggregated from timestamped activity. Employers see not just what was learned, but how much time was invested learning it.',
              },
            ].map((item, i) => (
              <div key={i} style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 18, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: `${G}15`, color: G, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 800, color: TX, margin: '0 0 6px' }}>{item.title}</h3>
                  <p style={{ fontSize: 13.5, color: MU, lineHeight: 1.65, margin: 0 }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ SECTION 3: The two-tier verification ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            How verification works
          </h2>
          <p style={{ color: MU, fontSize: 15, lineHeight: 1.7, margin: '0 0 24px' }}>
            Every credential has a public verification page with a QR code and URL.
            Anyone can confirm a credential is real in thirty seconds. With the
            learner's consent (a 4-digit PIN they provide), the full learning
            evidence is unlocked.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
            {/* Public tier */}
            <div style={{ background: D2, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 26px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Eye size={18} color={BLUE} />
                <h3 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: 0 }}>Public verification</h3>
              </div>
              <p style={{ fontSize: 11, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>
                No PIN required — anyone with the QR or URL
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  'The certificate is genuine (system-issued, not fabricated)',
                  'The learner\'s credential name',
                  'Each publicly visible credential with course name and mastery score',
                  'The date of issuance',
                  'The Still Waters Academy verification seal',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: MU }}>
                    <CheckCircle2 size={14} color={GR} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Deep tier */}
            <div style={{ background: D2, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 26px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Lock size={18} color={G} />
                <h3 style={{ fontSize: 16, fontWeight: 800, color: TX, margin: 0 }}>Deep verification</h3>
              </div>
              <p style={{ fontSize: 11, color: MU2, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>
                PIN required — the learner chooses who sees this
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  'The learner\'s verified photo',
                  'Complete assessment history — every score, every retry',
                  'All credentials, including ones the learner has kept private',
                  'Verified learning hours and evidence summary',
                  'Per-concept mastery breakdown (strongest and growth areas)',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: MU }}>
                    <CheckCircle2 size={14} color={G} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ lineHeight: 1.5 }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 16, padding: '16px 20px', marginTop: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <KeyRound size={16} color={BLUE} style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: 12.5, color: MU, lineHeight: 1.6, margin: 0 }}>
              <strong style={{ color: TX }}>The PIN is the learner's consent mechanism.</strong> Given deliberately
              to employers, universities, or NGOs they trust. If it leaks, the learner regenerates it instantly —
              the old PIN dies. The learner sees a view counter showing exactly how many times their deep record
              has been accessed, and by implication, whether their PIN has traveled further than intended.
            </p>
          </div>
        </section>

        {/* ═══ SECTION 4: Identity verification ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            Identity assurance
          </h2>
          <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 26px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                {
                  icon: <FileText size={16} />,
                  title: 'Locked credential name',
                  body: 'The name printed on every certificate is set once and locked. Changes require written request to the Academy — the same standard as official transcripts.',
                },
                {
                  icon: <Camera size={16} />,
                  title: 'Verified profile photo',
                  body: 'Learners upload a photo that appears on their deep verification page. Combined with the PIN, an employer confirms both the credential and the person presenting it.',
                },
                {
                  icon: <ShieldCheck size={16} />,
                  title: 'Proctored checkpoints (rolling out)',
                  body: 'For employer-grade identity claims, periodic verified assessment moments anchor the account to the human. Parents vouch for children; adult learners can opt into webcam-verified finals.',
                },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `${G}12`, color: G, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 800, color: TX, margin: '0 0 4px' }}>{item.title}</h3>
                    <p style={{ fontSize: 12.5, color: MU, lineHeight: 1.6, margin: 0 }}>{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ SECTION 5: For specific audiences ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            Who verifies, and what they see
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 14 }}>
            {[
              {
                icon: <Building2 size={18} />,
                title: 'Employers',
                body: 'Scan the QR on a resume. Confirm the credential is real in 30 seconds. With the PIN, review the full evidence — every assessment, every retry, verified learning hours.',
              },
              {
                icon: <GraduationCap size={18} />,
                title: 'Universities',
                body: 'Admissions officers can review a homeschool applicant\'s complete learning record — course by course, concept by concept — with more transparency than any high school transcript.',
              },
              {
                icon: <Users size={18} />,
                title: 'NGOs & Sponsors',
                body: 'Organizations funding education can demonstrate documented, verifiable learning outcomes to their boards and funders. Every sponsored seat produces evidence.',
              },
              {
                icon: <Search size={18} />,
                title: 'Parents',
                body: 'Full oversight of your child\'s learning record. Control their PIN, their photo, their public visibility — until they\'re old enough to manage it themselves.',
              },
            ].map((aud, i) => (
              <div key={i} style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 18, padding: '22px 20px' }}>
                <div style={{ width: 38, height: 38, borderRadius: 12, background: `${BLUE}12`, color: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  {aud.icon}
                </div>
                <h3 style={{ fontSize: 14.5, fontWeight: 800, color: TX, margin: '0 0 6px' }}>{aud.title}</h3>
                <p style={{ fontSize: 12.5, color: MU, lineHeight: 1.6, margin: 0 }}>{aud.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ SECTION 6: Standards & governance ═══ */}
        <section style={{ marginBottom: 56 }}>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)', fontWeight: 700, color: TX, margin: '0 0 16px' }}>
            Standards & governance
          </h2>
          <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '24px 26px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'Mastery threshold', value: '90% blended rolling score required for certificate issuance' },
                { label: 'Assessment integrity', value: 'Server-side generation and scoring; answer keys never reach the client' },
                { label: 'Retry policy', value: 'Unlimited dignified retakes; every attempt documented permanently' },
                { label: 'Data retention', value: 'Learning records are permanent; certificates never expire from the record' },
                { label: 'Name changes', value: 'Locked at issuance; written request to the Academy required for alteration' },
                { label: 'Privacy governance', value: 'Learner controls public visibility per credential; PIN gates deep evidence; photo shown only after PIN entry' },
                { label: 'Legal compliance', value: 'Admin override available for GDPR erasure, safeguarding, and legal requests' },
                { label: 'Audit trail', value: 'Every deep verification access is counted and visible to the learner' },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', paddingBottom: i < 7 ? 14 : 0, borderBottom: i < 7 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: MU2, textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: 140, flexShrink: 0, paddingTop: 2 }}>
                    {row.label}
                  </span>
                  <span style={{ fontSize: 13, color: TX, lineHeight: 1.5 }}>
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ CTA ═══ */}
        <section style={{ textAlign: 'center', paddingTop: 16 }}>
          <p style={{ fontFamily: serif, fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', fontWeight: 700, color: TX, margin: '0 0 12px', lineHeight: 1.3 }}>
            Don't take our word for it.
            <br />
            <span style={{ color: G }}>Verify the evidence.</span>
          </p>
          <p style={{ color: MU, fontSize: 14, lineHeight: 1.6, margin: '0 0 24px', maxWidth: 440, marginInline: 'auto' }}>
            Ask any Still Waters learner for their credential page.
            Scan the QR. Enter their PIN. See the work.
          </p>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 36px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 15, textDecoration: 'none' }}>
            Explore Still Waters Academy <ChevronRight size={16} />
          </Link>
        </section>

      </div>
    </div>
  )
}